import React from "react"
import useClientLogos from "../../clients/useClientLogos"

/**
 * Trusted By — src/Design/industries/industry-*.html, SECTION 5.
 * Sits between Results and Why Visiomate. Left column: "Trusted by"
 * heading. Right column: that page's own industry clients, resolved from
 * the one shared list (src/data/clientLogos.js).
 *
 * Deliberately NOT src/components/clients/ClientLogoBox.js (the homepage
 * marquee / About Us component) — that one sizes each logo's own fixed box
 * per aspect-ratio band (very-wide/wide/square-ish/tall, each a different
 * width AND height), which is exactly what made this row's gaps and logo
 * heights uneven. This section instead gives every logo ONE shared height
 * (width auto, capped by a max-width) so they truly line up, and never a
 * colour logo: it reads `grayImage` directly rather than `logoImage`
 * (which falls back to colour), applying a CSS grayscale filter only for
 * the handful of clients with no grey file yet.
 *
 * names: string[] — that page's OWN industry clients only, in the order
 * given, never other pages' clients as filler (a data-file decision, not
 * something this component adds to or trims). Each must be an exact
 * `name` from src/data/clientLogos.js.
 *
 * Renders nothing at all — no heading, no empty band — when a page has no
 * logos of its own (real estate, currently).
 */
const TrustedBy = ({ names = [] }) => {
  const clientLogos = useClientLogos()

  const clients = names
    .map(name => clientLogos.find(c => c.name === name))
    .filter(Boolean)
    .map(client => ({
      client,
      image: client.grayImage || client.colorImage,
      // No grey file for this client — its colour file stands in, grey-
      // filtered in CSS so no colour ever shows (clientLogos.js documents
      // which clients this applies to).
      filtered: !client.grayImage && !!client.colorImage,
    }))
    .filter(entry => entry.image)

  if (clients.length === 0) return null

  return (
    <section className="itrusted">
      <div className="itrusted__inner">
        <h2 className="itrusted__label">Trusted by</h2>
        <div className="itrusted__logos">
          {clients.map(({ client, image, filtered }) => (
            <img
              key={client.name}
              className={`itrusted__logo${filtered ? " itrusted__logo--filtered" : ""}`}
              src={image.fluid.srcWebp || image.fluid.src}
              srcSet={image.fluid.srcSetWebp || image.fluid.srcSet}
              sizes={image.fluid.sizes}
              alt={client.name}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default TrustedBy
